import React, { useEffect } from 'react';
import { Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';

import { FormInputText } from '../../../form-components/FormInputText';
import CommonModalButton from '../../../form-components/CommonModalButton';
import { adjustSmsBalance } from '../../../api/sms';
import {
  ModalBox,
  ModalHeader,
  ModalTitle,
  ModalBody,
  FieldRow,
  FieldLabel,
  FieldControl,
  FieldValue,
  PreviewText,
  ModalFooter,
} from './SmsBalanceAdjustModalStyled';

// 사용자는 항상 양수로 입력하고, 회수일 때만 부호를 뒤집어 보낸다.
const MODE_LABEL = {
  grant: { title: '포인트 지급', submit: '지급', sign: 1 },
  revoke: { title: '포인트 회수', submit: '회수', sign: -1 },
};

const SmsBalanceAdjustModal = ({ open, mode, branch, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.user);
  const toast = useToast();

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: { amount: '', description: '' },
  });

  useEffect(() => {
    if (open) reset({ amount: '', description: '' });
  }, [open, reset]);

  const modeInfo = MODE_LABEL[mode] || MODE_LABEL.grant;
  const currentBalance = branch?.balance ?? 0;
  const inputAmount = Number(String(watch('amount') || '').replace(/[^0-9]/g, '')) || 0;
  const afterBalance = currentBalance + modeInfo.sign * inputAmount;

  const adjustMutation = useMutation({
    mutationFn: form =>
      adjustSmsBalance(
        branch.branchId,
        { amount: modeInfo.sign * Number(form.amount), description: form.description?.trim() || undefined },
        user?.token,
      ),
    onSuccess: () => {
      toast.success(t(`${modeInfo.submit}되었습니다`));
      onSuccess();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || t('잔액 조정에 실패했습니다'));
    },
  });

  const onSubmit = form => {
    const amount = Number(String(form.amount || '').replace(/[^0-9]/g, ''));
    if (!amount) {
      toast.error(t('수량을 입력해주세요'));
      return;
    }
    if (mode === 'revoke' && amount > currentBalance) {
      toast.error(t('현재 잔액보다 많이 회수할 수 없습니다'));
      return;
    }
    adjustMutation.mutate({ ...form, amount });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalBox>
        <ModalHeader>
          <ModalTitle>{t(modeInfo.title)}</ModalTitle>
          <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </ModalHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <FieldRow>
              <FieldLabel>{t('지점')}</FieldLabel>
              <FieldValue>{branch?.branchName || '-'}</FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>{t('현재 잔액')}</FieldLabel>
              <FieldValue>{`${currentBalance.toLocaleString()} ${t('건')}`}</FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>{t('수량')}</FieldLabel>
              <FieldControl>
                <FormInputText
                  control={control}
                  name="amount"
                  placeholder={t('숫자만 입력')}
                  inputProps={{ inputMode: 'numeric' }}
                />
              </FieldControl>
            </FieldRow>

            <FieldRow>
              <FieldLabel />
              <PreviewText isNegative={afterBalance < 0}>
                {afterBalance < 0
                  ? t('현재 잔액보다 많이 회수할 수 없습니다')
                  : `${t('조정 후 잔액')} ${afterBalance.toLocaleString()} ${t('건')}`}
              </PreviewText>
            </FieldRow>

            <FieldRow>
              <FieldLabel>{t('사유')}</FieldLabel>
              <FieldControl>
                <FormInputText control={control} name="description" placeholder={t('조정 사유를 입력해주세요')} />
              </FieldControl>
            </FieldRow>
          </ModalBody>

          <ModalFooter>
            <CommonModalButton variant="cancel" type="button" onClick={onClose}>
              {t('취소')}
            </CommonModalButton>
            <CommonModalButton variant="submit" type="submit" disabled={adjustMutation.isPending}>
              {t(modeInfo.submit)}
            </CommonModalButton>
          </ModalFooter>
        </form>
      </ModalBox>
    </Modal>
  );
};

export default SmsBalanceAdjustModal;
